/**
 * RegisteredCompaniesServicesHub — iter-249 (Feb 2026)
 * ====================================================
 * Landing hub for the 5-stage service-request lifecycle. Lives under the
 * Cloud GM Company Agent hub (iter-253) so it shares the same header,
 * breadcrumb and back-button behaviour as "تسجيل الشركات" and "ملف الشركات".
 *
 * Path: الرئيسية → CLOUDGM FZ LLC (للإشراف) → Cloud GM Company Agent →
 *       خدمات الشركات المسجلة
 *
 * Sub-pages (also reuse AgentPageShell):
 *   /registered-companies-services/new       → ServiceRequestForm
 *   /registered-companies-services/tracking  → ServiceRequestsTracking
 *   /registered-companies-services/:id       → ServiceRequestDetail
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, PlusCircle, ClipboardList } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { AgentPageShell } from '../agent/AgentPageShell';

export default function RegisteredCompaniesServicesHub() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const breadcrumb = [
    { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
    { label_ar: 'CLOUDGM FZ LLC (للإشراف)', label_en: 'CLOUDGM FZ LLC (Supervision)', path: '/dashboard?platform=fzlc' },
    { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
    { label_ar: 'خدمات الشركات المسجلة', label_en: 'Registered Companies Services', path: null },
  ];

  return (
    <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/manage/cloudgm-agent')}>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white" data-testid="rcs-hub-title">
              {t('خدمات الشركات المسجلة', 'Registered Companies Services')}
            </h1>
            <p className="text-sm text-white/50 mt-0.5">
              {t(
                'أدرج طلبات الخدمة للشركات المسجلة وتابعها عبر 5 مراحل تنفيذية.',
                'Insert service requests for registered companies and track them across 5 sequential stages.',
              )}
            </p>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <motion.button
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          onClick={() => navigate('/registered-companies-services/new')}
          className="text-start p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-emerald-500/40 transition-all"
          data-testid="rcs-hub-insert-btn"
        >
          <div className="w-14 h-14 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4">
            <PlusCircle className="h-7 w-7 text-emerald-400" />
          </div>
          <h3 className="text-lg font-bold text-white mb-1">
            {t('إدراج خدمة لشركة مسجلة', 'Insert Company Service Request')}
          </h3>
          <p className="text-[12px] text-white/50">
            {t(
              'ابدأ طلب خدمة جديد لشركة مسجلة (يدخل مرحلة "تم استلام الطلب" بدون إصدار فاتورة فورية).',
              'Start a new service request for a registered company (enters "Request Received" without issuing an immediate invoice).',
            )}
          </p>
        </motion.button>

        <motion.button
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          onClick={() => navigate('/registered-companies-services/tracking')}
          className="text-start p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-cyan-500/40 transition-all"
          data-testid="rcs-hub-tracking-btn"
        >
          <div className="w-14 h-14 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4">
            <ClipboardList className="h-7 w-7 text-cyan-400" />
          </div>
          <h3 className="text-lg font-bold text-white mb-1">
            {t('متابعة الطلبات', 'Order Tracking & Pipeline')}
          </h3>
          <p className="text-[12px] text-white/50">
            {t(
              'لوحة متابعة مباشرة لكل الطلبات عبر المراحل: استلام ← قبول ← تنفيذ ← تحصيل ← إرسال.',
              'Live board of every request across the pipeline: Received → Accepted → In Progress → Fee Collection → Dispatched.',
            )}
          </p>
        </motion.button>
      </div>
    </AgentPageShell>
  );
}
